<script lang="ts">
  // Universal-TopBar mit drei klickbaren Zonen + integrierter Suche:
  //   - Brand-Symbol links: Klick oeffnet Brand-Popover (Top-Sektionen).
  //   - Breadcrumb mittig: jedes Segment klickbar, letztes (Leaf)
  //     oeffnet Sub-Pages-Popover. Long-Path kollabiert mit "…".
  //   - Such-Pille rechts: immer sichtbar. Fokus / ⌘K / "/" oeffnet
  //     ein Dropdown unter der TopBar (Browse bei leerem Query,
  //     scorierte Treffer bei Eingabe -- inkl. Aliase und Fuzzy).
  //     Loest das frueher als Overlay sichtbare Cmd-K-Fenster ab.
  //   - Zahnrad rechts: Sprung in die Einstellungen.

  import type { Snippet } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { anchoredPopover } from "./anchored-popover";
  import { getClusterStats } from "./cluster-stats.svelte";
  import MediaJobsPanel from "./MediaJobsPanel.svelte";
  import NavSearchPanel from "./NavSearchPanel.svelte";
  import {
    buildCrumbs,
    sectionForPath,
    subPagesFor,
  } from "./nav-sections";
  import { getPageNav } from "./page-nav.svelte";
  import { portal } from "./portal";
  import { router } from "./router.svelte";
  import { findRoute } from "./routes";
  import AiIndicator from "./AiIndicator.svelte";
  import { t } from "./i18n.svelte";
  import { getBypass, beendeBypass } from "./bypass-summary.svelte";
  import { getAlertsVisible, ackAlert, ackAllVisible } from "./alerts-store.svelte";
  import { quittierbar, type Alert } from "./meldungs-logik";
  import Icon from "./Icon.svelte";
  import { formatRelative } from "./format";

  const nav = $derived(getPageNav());
  // Bypass-Schloss: klickbares Icon oeffnet Popover mit Details + Deaktivieren.
  const bypass = $derived(getBypass());
  const bypassActive = $derived(!!bypass?.active);
  const bypassRemaining = $derived.by(() => {
    const s = bypass?.remainingS ?? 0;
    if (!bypass || bypass.autoOffMin <= 0 || s <= 0) return null;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
  });
  let bypassBusy = $state(false);
  async function deactivate() {
    if (bypassBusy) return;
    bypassBusy = true;
    try {
      // Gescheitert: beendeBypass meldet den Grund, das Menü bleibt offen.
      if (await beendeBypass()) openMenu = null;
    } finally {
      bypassBusy = false;
    }
  }

  // Warn-Indikator: alle aktiven nicht-kritischen Alerts aus dem shared Store.
  const alertsVisible = $derived(getAlertsVisible());
  const warnCount = $derived(alertsVisible.length);
  // Eine Beschriftung fuer beide Stellen: den title des Dreiecks (wo die
  // Zahl steht, weil das Icon selbst keine Bubble traegt) und den Kopf
  // des Popovers.
  const warnLabel = $derived(
    warnCount === 1
      ? t("alerts.count.one", "1 Warnung aktiv")
      : t("alerts.count.many", "{n} Warnungen aktiv").replace("{n}", String(warnCount)),
  );
  let now = $state(new Date());
  // Mit Aufräumen: die TopBar wird bei jedem Wechsel über die 900-px-Grenze
  // (iPad drehen, Split View) neu eingehängt, und jedes Mal blieb ein
  // Intervall zurück.
  $effect(() => {
    const id = setInterval(() => (now = new Date()), 30_000);
    return () => clearInterval(id);
  });
  let acksInFlight = $state(false);

  async function handleAckAll() {
    if (acksInFlight) return;
    acksInFlight = true;
    try { await ackAllVisible(); } finally { acksInFlight = false; }
  }
  async function handleAck(a: Alert) {
    await ackAlert(a);
  }

  // Zeilen-Klick im Warn-Popover fuehrt auf die Meldungen-Seite und
  // klappt dort die Zeile auf (?open). Kein Modal -- das Detail lebt als
  // Zeilen-Erweiterung auf der Seite, das Popover ist der Schnellzugriff.
  function openAlert(a: Alert) {
    openMenu = null;
    window.location.hash = `/system/meldungen?open=${encodeURIComponent(a.id)}`;
  }
  const isRoot = $derived(router.current === "/" || router.current === "");

  // Host-Detail-Sonderweg: /infra/cluster/<host_id> ist eine Param-Route
  // mit Label "Host" in routes.ts. Im Breadcrumb soll dort der echte
  // Host-Name stehen (Forge statt Host), und das Tail-Popover soll die
  // Geschwister-Hosts anbieten statt der Section-Sub-Pages.
  const hostRouteId = $derived.by(() => {
    const m = router.current.match(/^\/infra\/cluster\/([^/]+)$/);
    if (!m) return null;
    // Statische Sub-Routen (Register wie /inventar, /ssh) sind keine
    // Hosts -- nur wenn die Param-Route :host_id matcht, greift der
    // Host-Sonderweg.
    const r = findRoute(router.current);
    if (r && !r.route.path.includes(":")) return null;
    return decodeURIComponent(m[1]);
  });
  const hostRouteHost = $derived.by(() => {
    if (!hostRouteId) return null;
    const clusters = getClusterStats();
    if (!clusters) return null;
    for (const c of clusters) {
      for (const h of c.hosts) {
        if (h.id === hostRouteId) return h;
      }
    }
    return null;
  });

  const crumbs = $derived.by(() => {
    const base = buildCrumbs(router.current);
    // Sensor-Detail-Pages (Klima/CO2/Lipoguard/Waterleak) deklarieren
    // backTo=/zuhause/sensoren und setzen den Page-Title auf das gewaehlte
    // Geraet. Breadcrumb soll IoT > Sensoren > <Geraet> sein statt des
    // generischen Domain-Labels -- so kommt man ueber den Pfad zurueck
    // zu den Sensoren, und der Geraetename steht schon oben (eigener
    // Page-Header entfaellt).
    if (nav.backTo === "/zuhause/sensoren" && base.length > 0) {
      // Geraetename direkt aus ?sensor= lesen -- deterministisch und
      // ohne Race mit App.svelte's Title-Reset (der getPageTitle leert).
      const sensor = router.query.sensor;
      return [
        base[0],
        { label: nav.backLabel || t("nav.sensoren", "Sensoren"), href: "/zuhause/sensoren" },
        { label: sensor || base[base.length - 1].label, leaf: true },
      ];
    }
    if (hostRouteId && base.length > 0) {
      const name = hostRouteHost?.name || hostRouteId;
      const last = base[base.length - 1];
      if (last.leaf) {
        return [...base.slice(0, -1), { ...last, label: name }];
      }
    }
    return base;
  });

  // Crumb-Truncation wie zuvor.
  const COLLAPSE_AT = 4;
  const collapsed = $derived(crumbs.length > COLLAPSE_AT);
  const headCrumbs = $derived(collapsed ? crumbs.slice(0, 1) : crumbs);
  const midCrumbs = $derived(collapsed ? crumbs.slice(1, -1) : []);
  const tailCrumbs = $derived(collapsed ? crumbs.slice(-1) : []);

  // Popover-Slots fuer Tail-Crumb / Mid-Crumb / Page-Info / Lock / Warns.
  // Die Page-Info ("info") haengt seit 2026-07-04 am Brand-Schild links
  // und erscheint als Leiste am unteren Fensterrand -- der Trigger ist
  // brandTrigger, das Panel ist das an <body> portalte Info-Modal.
  type MenuKey = "tail" | "mid" | "info" | "lock" | "warns";
  let openMenu = $state<MenuKey | null>(null);

  let brandTrigger = $state<HTMLButtonElement | undefined>();
  let tailTrigger = $state<HTMLButtonElement | undefined>();
  let midTrigger = $state<HTMLButtonElement | undefined>();
  let lockTrigger = $state<HTMLButtonElement | undefined>();
  let warnsTrigger = $state<HTMLButtonElement | undefined>();

  function toggleMenu(which: MenuKey) {
    openMenu = openMenu === which ? null : which;
  }

  // Page-Wechsel schliesst alle Popover + die Suche.
  $effect(() => {
    router.current;
    openMenu = null;
    searchOpen = false;
    query = "";
  });

  // Outside-Klick + Escape schliessen Brand/Tail/Mid-Popover.
  // Die Popover haengen seit 26.07. per anchoredPopover am <body> und sind
  // damit KEINE DOM-Nachfahren ihres Triggers mehr -- die Zugehoerigkeit
  // traegt deshalb das Markup selbst (data-topbar-popover) statt einer
  // Referenz je Panel.
  $effect(() => {
    if (!openMenu) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node | null;
      const trigger =
        openMenu === "tail"  ? tailTrigger  :
        openMenu === "info"  ? brandTrigger :
        openMenu === "lock"  ? lockTrigger  :
        openMenu === "warns" ? warnsTrigger :
        midTrigger;
      const el = t instanceof Element ? t : (t?.parentElement ?? null);
      if (el?.closest("[data-topbar-popover]")) return;
      if (trigger?.contains(t ?? null)) return;
      openMenu = null;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") openMenu = null;
    };
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  });

  const tailSubs = $derived.by(() => {
    // Host-Page: Geschwister-Liste statt Section-Sub-Pages. Pseudo-Hosts
    // (online === null und kein Stats-Eintrag, z.B. Discovery-only)
    // bleiben drin, damit die Liste mit der Cluster-Page deckungsgleich
    // ist; der eigene Host wird im Popover als active markiert.
    if (hostRouteId) {
      const clusters = getClusterStats() ?? [];
      const subs: { label: string; href: string; depth: number; hidden: boolean }[] = [];
      for (const c of clusters) {
        for (const h of c.hosts) {
          subs.push({
            label: h.name || h.id,
            href: `/infra/cluster/${encodeURIComponent(h.id)}`,
            depth: 0,
            hidden: false,
          });
        }
      }
      return subs;
    }
    const sec = sectionForPath(router.current);
    if (!sec) return [];
    const subs = subPagesFor(sec);
    return subs.map((s) => {
      let depth = 0;
      for (const other of subs) {
        if (other.href !== s.href && s.href.startsWith(other.href + "/")) {
          depth++;
        }
      }
      return { ...s, depth };
    });
  });
  const tailSectionLabel = $derived.by(() => {
    if (hostRouteId) return t("nav.hosts_section_header", "Hosts");
    return sectionForPath(router.current)?.label ?? "";
  });

  function go(href: string): void {
    openMenu = null;
    closeSearch();
    window.location.hash = href;
  }

  // === Such-Pille ============================================
  // searchOpen: Dropdown ist sichtbar; wird ueber Fokus +
  // Hotkeys gesteuert. query: Eingabewert (leer = Browse-Modus).
  // active: aktueller Tastatur-Index in der Trefferliste.
  let searchOpen = $state(false);
  let query = $state("");
  let active = $state(0);
  let filteredCount = $state(0);
  let searchInput = $state<HTMLInputElement | undefined>();
  let searchWrap = $state<HTMLDivElement | undefined>();
  // Das Panel haengt am <body> (anchoredPopover) und ist damit kein
  // Nachfahre von searchWrap mehr -- der Outside-Klick braucht es extra.
  let searchPanel = $state<HTMLDivElement | undefined>();

  // Platzhalter passend zur Feldbreite. Der alte Text war 305 px breit
  // bei einem 272-px-Feld -- er wurde AUF JEDER Breite abgeschnitten
  // ("… was du tur").
  //
  // Bewusst OHNE angehaengtes "…": ein Platzhalter, der auf drei Punkte
  // endet, sieht abgeschnitten aus -- also genau nach dem Fehler, den
  // die Kuerzung beheben soll.
  let wideSearch = $state(false);
  $effect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    wideSearch = mq.matches;
    const on = (e: MediaQueryListEvent) => (wideSearch = e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  });
  const searchPlaceholder = $derived(
    wideSearch
      ? t("cmdk.placeholder", "Suchen oder Aktion")
      : t("cmdk.placeholder_short", "Suchen"),
  );

  function openSearch(): void {
    searchOpen = true;
    queueMicrotask(() => searchInput?.focus());
  }

  function closeSearch(): void {
    searchOpen = false;
    query = "";
    active = 0;
    searchInput?.blur();
  }

  function toggleSearch(): void {
    if (searchOpen) closeSearch();
    else openSearch();
  }

  // Globale Hotkeys: ⌘K toggelt, "/" oeffnet, Esc schliesst.
  // Rechtsklick (ausser auf editierbaren Feldern) oeffnet die Suche.
  $effect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        toggleSearch();
        return;
      }
      if (e.key === "/" && !searchOpen) {
        const t = e.target as HTMLElement | null;
        if (t?.matches("input, textarea, [contenteditable='true']")) return;
        e.preventDefault();
        openSearch();
        return;
      }
      if (searchOpen && e.key === "Escape") {
        e.preventDefault();
        closeSearch();
      }
    };
    const onCtx = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest("input, textarea, [contenteditable='true']")) return;
      e.preventDefault();
      openSearch();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("contextmenu", onCtx);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("contextmenu", onCtx);
    };
  });

  // Outside-Klick schliesst die Suche (Klick im Wrap inkl. Dropdown
  // gehoert dazu -- nur Klicks ausserhalb beenden).
  $effect(() => {
    if (!searchOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (searchWrap?.contains(target ?? null)) return;
      if (searchPanel?.contains(target ?? null)) return;
      closeSearch();
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  });

  // Pfeil hoch/runter + Enter (nur bei aktiver Suche mit Query).
  function onSearchKey(e: KeyboardEvent) {
    if (!query) {
      // ⌘K-Toggle wird global gehandhabt; hier nur die Page-Nav.
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      active = Math.min(active + 1, Math.max(0, filteredCount - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      active = Math.max(active - 1, 0);
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Klick auf den aktiven Treffer triggern -- NavSearchPanel
      // rendert die Buttons mit data-idx.
      const node = document.querySelector<HTMLButtonElement>(
        `[data-search-results] [data-idx="${active}"]`,
      );
      node?.click();
    }
  }

  // Beim Aendern der Query Auswahl zuruecksetzen.
  $effect(() => {
    query;
    active = 0;
  });

  // Scroll den aktiven Treffer in den sichtbaren Bereich.
  $effect(() => {
    if (!searchOpen || !query) return;
    const node = document.querySelector<HTMLElement>(
      `[data-search-results] [data-idx="${active}"]`,
    );
    node?.scrollIntoView({ block: "nearest" });
  });
</script>

{#snippet popoverShell(label: string, anchor: HTMLElement | undefined, content: Snippet)}
  <div
    use:anchoredPopover={{ anchor, align: "left" }}
    data-topbar-popover
    class="border-border bg-bg-elev z-40 w-[min(22rem,calc(100vw-2rem))] overflow-y-auto rounded-xl border p-2 shadow-xl"
    role="dialog"
    aria-label={label}
  >
    {@render content()}
  </div>
{/snippet}

{#snippet tailPopoverContent()}
  <div class="text-fg-subtle px-2 pb-1 pt-1.5 t-eyebrow">
    {tailSectionLabel}
  </div>
  <div class="grid gap-0.5">
    {#each tailSubs as sub (sub.href)}
      <a
        href={`#${sub.href}`}
        onclick={(e) => {
          e.preventDefault();
          go(sub.href);
        }}
        style={sub.depth > 0
          ? `padding-left: ${0.5 + sub.depth * 0.85}rem`
          : undefined}
        class="text-fg hover:bg-bg-muted flex items-center rounded-md ctrl-s.5 text-sm transition {router.current ===
        sub.href
          ? 'bg-accent/15'
          : ''} {sub.depth > 0 ? 'text-fg-muted' : ''}"
      >
        {#if sub.depth > 0}
          <span class="text-fg-subtle mr-1.5 t-meta" aria-hidden="true">└</span>
        {/if}
        {sub.label}
      </a>
    {/each}
  </div>
{/snippet}

{#snippet midPopoverContent()}
  <div class="grid gap-0.5">
    {#each midCrumbs as c (c.href ?? c.label)}
      {#if c.href}
        <a
          href={`#${c.href}`}
          onclick={(e) => {
            e.preventDefault();
            go(c.href!);
          }}
          class="text-fg hover:bg-bg-muted block truncate rounded-md ctrl-s.5 text-sm transition"
        >
          {c.label}
        </a>
      {:else}
        <span
          class="text-fg-muted block truncate rounded-md px-2 py-1.5 text-sm"
        >
          {c.label}
        </span>
      {/if}
    {/each}
  </div>
{/snippet}

{#snippet tailButton(label: string)}
  <!-- Zwei Dinge sind noetig, damit der Krumen bei schmalem Viewport
       kuerzt statt quer ueber Warn-Icon und Such-Pille zu malen:
         min-w-0 am Wrapper -- sonst behaelt er als Flex-Item (Default
           min-width:auto) seine Inhaltsbreite und schrumpft nie;
         w-full am Button -- ein <button> hat intrinsische Breite und
           fuellt den geschrumpften Wrapper NICHT von selbst, anders als
           ein <div>. Ohne das bleibt er auf Inhaltsbreite stehen und
           das truncate am Label greift nie.
       KEIN overflow-hidden hier oder an der <nav>: das wuerde das
       Popover darunter abschneiden. -->
  <div class="relative min-w-0">
    <button
      type="button"
      bind:this={tailTrigger}
      onclick={() => toggleMenu("tail")}
      aria-haspopup="dialog"
      aria-expanded={openMenu === "tail"}
      class="text-fg flex w-full max-w-[16rem] items-center rounded-md px-1.5 py-0.5 font-medium transition hover:bg-bg-muted/60 {openMenu ===
      'tail'
        ? 'bg-bg-muted/60'
        : ''}"
    >
      <!-- Nur das Label kuerzen: laege truncate am Button, wuerde die
           Ellipse als Erstes den Aufklapp-Pfeil verschlucken. -->
      <span class="truncate">{label}</span>
      <span class="text-fg-subtle ml-1 shrink-0 text-base leading-none" aria-hidden="true">▾</span>
    </button>
    {#if openMenu === "tail" && tailSubs.length > 0}
      {@render popoverShell(tailSectionLabel, tailTrigger, tailPopoverContent)}
    {/if}
  </div>
{/snippet}

<!-- Deckend statt Frost (2026-07): backdrop-blur auf der sticky Pille
     war die letzte persistente backdrop-filter-Ebene -- ueber dem
     driftenden Hintergrund musste Safari den Frost laufend neu
     rastern (gleiche Kostenklasse wie der entfernte bg-Blur, s.
     lesson_safari_animated_blur). `surface` (statt bg-bg-elev) haelt
     die Pille konsistent mit den Karten UND folgt der Karten-Deckkraft
     (--card-opacity) -- weiter ohne backdrop-blur. -->
<header
  class="border-border surface sticky top-2 z-30 mx-auto mb-6 flex h-12 items-center gap-1 rounded-xl border px-3 py-1.5 shadow-sm"
  aria-label="Navigation"
>
  <!-- Brand-Schild links: Logo + (auf Dashboard) Wortmarke. Klick oeffnet
       die Page-Info als Leiste am unteren Fensterrand (statt eines eigenen
       ⓘ-Buttons rechts, der auf infolosen Pages fehlte). -->
  <div class="relative shrink-0">
    <button
      type="button"
      bind:this={brandTrigger}
      onclick={() => toggleMenu("info")}
      aria-haspopup="dialog"
      aria-expanded={openMenu === "info"}
      aria-label={nav.infoTitle ?? t("nav.pageinfo", "Über diese Seite")}
      title={nav.infoTitle ?? t("nav.pageinfo", "Über diese Seite")}
      class="text-accent hover:text-fg flex items-center gap-2 rounded-md px-1 py-1 transition {openMenu ===
      'info'
        ? 'text-fg'
        : ''}"
    >
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        fill="none"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-accent h-7 w-7 shrink-0"
        aria-hidden="true"
      >
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1 18.94 18.94 0 0 0 7-1.95 18.94 18.94 0 0 0 7 1.95 1 1 0 0 1 1 1z" />
      </svg>
      <!-- Wortmarke nur auf root: haelt die TopBar auf dem Dashboard ruhig
           (statt eines Auswahl-Controls). Die Dashboard-Wahl lebt jetzt als
           Tab-Strip auf der Seite selbst (Dashboard.svelte). -->
      {#if isRoot}
        <span class="text-accent text-sm font-bold tracking-tight">homelab</span>
      {/if}
    </button>
  </div>

  {#if !isRoot}
    <a
      href="#/"
      onclick={(e) => {
        e.preventDefault();
        go("/");
      }}
      class="text-fg-subtle hover:text-accent hover:bg-bg-muted/60 flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition"
      aria-label={t("nav.dashboard", "Dashboard")}
      title={t("nav.dashboard", "Dashboard")}
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M3 12 12 3l9 9M5 10v10h14V10" />
      </svg>
    </a>
  {/if}

  {#if !isRoot && crumbs.length > 0}
    <!-- Unter sm sind die Vorfahren ausgeblendet -- dann darf auch das
         fuehrende Trennzeichen nicht stehenbleiben und ins Leere zeigen. -->
    <div
      class="text-fg-subtle hidden shrink-0 select-none text-xs sm:block"
      aria-hidden="true"
    >
      ›
    </div>
    <!-- Unter sm bleibt vom Pfad nichts Brauchbares uebrig: neben Logo,
         Warn-Icon, Suche und Profil (alle shrink-0) blieben der Nav
         36 px, also ein einzelner Buchstabe plus Pfeil. Lieber ganz weg
         -- wo man ist, sagen auf dem Handy die DomainNavBar der Seite
         und die untere Leiste. -->
    <nav
      class="hidden min-w-0 items-center gap-0.5 text-sm sm:flex"
      aria-label="Pfad"
    >
      {#each headCrumbs as c, i (i + ":head")}
        {@const isLast =
          !collapsed &&
          i === headCrumbs.length - 1 &&
          tailCrumbs.length === 0}
        {#if isLast}
          {@render tailButton(c.label)}
        {:else if c.href && !c.leaf}
          <a
            href={`#${c.href}`}
            class="text-fg-muted hover:text-fg hover:bg-bg-muted/60 hidden min-w-0 max-w-[16rem] truncate rounded-md px-1 py-0.5 transition sm:block"
          >
            {c.label}
          </a>
        {:else}
          <span class="text-fg hidden min-w-0 max-w-[16rem] truncate font-medium sm:block"
            >{c.label}</span
          >
        {/if}
        {#if i < headCrumbs.length - 1 || collapsed || tailCrumbs.length > 0}
          <span
            class="text-fg-subtle hidden shrink-0 select-none text-xs sm:block"
            aria-hidden="true">›</span
          >
        {/if}
      {/each}

      {#if collapsed}
        <!-- Auf dem Handy zeigt die Leiste nur noch die aktuelle Ebene:
             die Vorfahren waren dort auf 9px zusammengequetscht -- als
             Orientierung wertlos, aber breit genug, um die Such-Pille zu
             ueberlaufen. Der Zurueck-Weg haengt an der Domain-NavBar der
             Seite, nicht hier. -->
        <div class="relative hidden sm:block">
          <button
            type="button"
            bind:this={midTrigger}
            onclick={() => toggleMenu("mid")}
            aria-haspopup="dialog"
            aria-expanded={openMenu === "mid"}
            aria-label="Zwischenebenen anzeigen"
            class="text-fg-subtle hover:text-fg hover:bg-bg-muted/60 rounded-md px-1.5 py-0.5 transition {openMenu ===
            'mid'
              ? 'bg-bg-muted/60 text-fg'
              : ''}"
          >
            …
          </button>
          {#if openMenu === "mid" && midCrumbs.length > 0}
            {@render popoverShell("Zwischenebenen", midTrigger, midPopoverContent)}
          {/if}
        </div>
        <span
          class="text-fg-subtle hidden shrink-0 select-none text-xs sm:block"
          aria-hidden="true">›</span
        >
      {/if}

      {#each tailCrumbs as c, i (i + ":tail")}
        {@render tailButton(c.label)}
      {/each}
    </nav>
  {/if}

  <!-- Spacer schiebt Such-Pille + Aktionen + Profil nach rechts. -->
  <div class="min-w-0 flex-1"></div>

  <!-- Inline-Suche. Eingabefeld immer sichtbar (auf Mobile schrumpft
       es); Fokus oeffnet das Dropdown direkt unter der TopBar. Wrapper
       ist Klick-Boundary fuer Outside-Click UND Anker fuer die
       anchoredPopover-Positionierung; das Panel selbst haengt am <body>
       (Begruendung in anchored-popover.ts). -->
  <!-- Warn-Indikator: aktive warn-Level-Events, hinter Dreieck-Icon versteckt.
       OHNE Zahlen-Bubble (Tim 23.08.): das Dreieck erscheint nur, wenn
       wirklich etwas offen ist -- seine Anwesenheit IST das Signal, und
       eine zweistellige Marke am Rand macht die Kopfzeile unruhiger als
       die Information wert ist. Die Zahl steht im Kopf des Popovers und
       im title-Attribut, also genau dort, wo man sie sucht.
       Was hier NIE mitzählt: selbst erledigte Meldungen -- die stehen
       nur im Rückblick (alerts-store: selbstErledigt). -->
  {#if warnCount > 0}
    <div class="relative">
      <button
        type="button"
        bind:this={warnsTrigger}
        onclick={() => toggleMenu("warns")}
        aria-haspopup="dialog"
        aria-expanded={openMenu === "warns"}
        title={warnLabel}
        class="text-warning hover:bg-warning/10 relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition {openMenu === 'warns' ? 'bg-warning/10' : ''}"
      >
        <Icon name="alert-tri" size={15} />
      </button>
      {#if openMenu === "warns"}
        <div
          use:anchoredPopover={{ anchor: warnsTrigger, align: "right" }}
          data-topbar-popover
          class="border-border bg-bg-elev z-40 flex w-[min(26rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border p-2 shadow-xl"
          role="dialog"
          aria-label={t("alerts.popover_aria", "Aktive Warnungen")}
        >
          <div class="flex shrink-0 items-center justify-between px-2 pb-1 pt-1.5">
            <span class="t-eyebrow text-fg-subtle">{warnLabel}</span>
            <!-- Ab der ERSTEN Meldung, nicht erst ab der zweiten (Tim
                 21.08., dieselbe Regel wie auf der Meldungen-Seite). Ein
                 Bedienelement, das je nach Zeilenzahl da ist oder nicht,
                 muss man jedes Mal neu suchen. -->
            <button
              type="button"
              onclick={handleAckAll}
              disabled={acksInFlight}
              class="t-eyebrow text-fg-subtle hover:text-fg rounded ctrl-s hover:bg-bg-muted disabled:opacity-50"
            >
              {acksInFlight ? "…" : t("alerts.ack_all", "alle gelesen")}
            </button>
          </div>
          <!-- Deckel kommt von anchoredPopover (echter Platz unter der
               Pille); hier nur noch "darf schrumpfen und scrollen". -->
          <div class="min-h-0 flex-1 overflow-y-auto">
            {#each alertsVisible as a (a.id)}
              {@const cls = a.severity === "critical" || a.severity === "problem" ? "text-danger" : a.severity === "warning" ? "text-warning" : "text-fg-muted"}
              <div class="hover:bg-bg-muted/40 flex items-stretch rounded-md transition">
                <!-- Ganze Zeile oeffnet das Detail-Overlay. "gelesen"
                     bleibt als Schnellaktion daneben -- getrennte Buttons,
                     nicht verschachtelt. -->
                <button
                  type="button"
                  onclick={() => openAlert(a)}
                  class="flex min-w-0 flex-1 items-start gap-2 rounded-l-md px-2 py-1.5 text-left"
                >
                  <span class="mt-0.5 shrink-0 {cls}">
                    <Icon name={a.severity === "critical" || a.severity === "problem" ? "alert-octa" : a.severity === "info" ? "info" : "alert-tri"} size={14} />
                  </span>
                  <span class="min-w-0 flex-1">
                    <span class="t-meta text-fg block leading-tight">{a.title}</span>
                    {#if a.detail}
                      <span class="t-eyebrow text-fg-subtle mt-0.5 block truncate">{a.detail}</span>
                    {/if}
                    {#if a.ts}
                      <span class="t-eyebrow text-fg-subtle/70 mt-0.5 block tabular-nums">{formatRelative(a.ts, now)}</span>
                    {/if}
                  </span>
                  <span class="text-fg-subtle mt-0.5 shrink-0" aria-hidden="true">
                    <Icon name="arrow-right" size={13} />
                  </span>
                </button>
                {#if quittierbar(a)}
                  <button
                    type="button"
                    onclick={() => handleAck(a)}
                    class="t-eyebrow text-fg-subtle hover:text-fg shrink-0 self-center rounded px-1.5 py-0.5 hover:bg-bg-muted"
                    title={t("alerts.ack_hint", "Als gelesen markieren")}
                  >{t("alerts.ack_short", "gelesen")}</button>
                {/if}
              </div>
            {/each}
          </div>
          <!-- Fussleiste: Weg in den vollen Verlauf (auch gelesene). -->
          <div class="border-border mt-1 shrink-0 border-t pt-1">
            <a
              href="#/system/meldungen"
              onclick={() => (openMenu = null)}
              class="text-fg-muted hover:bg-bg-muted flex items-center gap-1.5 rounded-md ctrl-s.5 t-meta font-medium transition"
            >
              <Icon name="history" size={13} />
              {t("alerts.all_messages", "Alle Meldungen & Verlauf")}
            </a>
          </div>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Bypass-Schloss: klickbar, Popover mit Details + Deaktivieren. -->
  {#if bypassActive}
    <div class="relative">
      <button
        type="button"
        bind:this={lockTrigger}
        onclick={() => toggleMenu("lock")}
        aria-haspopup="dialog"
        aria-expanded={openMenu === "lock"}
        title={t("security.bypass.topbar", "API-Bypass aktiv")}
        class="text-warning hover:bg-warning/10 flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition {openMenu === 'lock' ? 'bg-warning/10' : ''}"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 9.9-1" />
        </svg>
      </button>
      {#if openMenu === "lock"}
        <div
          use:anchoredPopover={{ anchor: lockTrigger, align: "right" }}
          data-topbar-popover
          class="border-border bg-bg-elev z-40 w-72 overflow-y-auto rounded-xl border p-3 shadow-xl"
          role="dialog"
          aria-label="API-Bypass"
        >
          <p class="text-warning t-eyebrow mb-1">
            {t("security.bypass.title", "API-Zugriff ohne Anmeldung aktiv")}
          </p>
          <p class="text-fg-muted t-meta">
            {t("security.bypass.body", "Anfragen von localhost/Docker umgehen die Passkey-Anmeldung.")}
          </p>
          {#if bypassRemaining}
            <p class="text-fg-muted t-meta mt-1 tabular-nums">
              {t("security.bypass.autooff", "Automatisch aus in")} {bypassRemaining}.
            </p>
          {:else}
            <p class="text-fg-muted t-meta mt-1">
              {t("security.bypass.notimer", "Kein Auto-Aus gesetzt.")}
            </p>
          {/if}
          <button
            type="button"
            onclick={deactivate}
            disabled={bypassBusy}
            class="border-warning/50 text-warning hover:bg-warning/10 mt-3 w-full rounded-md border ctrl-m t-meta font-medium transition disabled:opacity-50"
          >
            {bypassBusy ? "…" : t("security.bypass.disable", "Jetzt deaktivieren")}
          </button>
        </div>
      {/if}
    </div>
  {/if}

  <!-- ms-2: das Warn-Badge sitzt als absolute Marke -right-1 ausserhalb
       seines Buttons und frisst damit genau den gap-1 der Kopfzeile --
       Dreieck und Such-Pille klebten aneinander. -->
  <div class="ms-2 shrink-0" bind:this={searchWrap}>
    <div
      class="border-border/70 bg-bg-muted/50 hover:border-accent/30 focus-within:border-accent/50 focus-within:bg-bg-muted/70 flex h-8 items-center gap-1.5 rounded-md border px-2 transition"
    >
      <!-- Das Lama als Pille-Icon UND Status-Indikator: es loest die
           alte Lupe ab, denn die Pille ist mehr als nur Suche, sie ist
           der KI-Einstieg. Ohne onopen -- ein Klick wuerde hier den
           Fokus aus dem Suchfeld reissen. Form + Zustaende stecken in
           AiIndicator.svelte, damit Telefon und Palette dasselbe Lama
           zeigen. -->
      <AiIndicator size={14} />
      <input
        bind:this={searchInput}
        bind:value={query}
        onfocus={openSearch}
        onkeydown={onSearchKey}
        type="text"
        role="combobox"
        aria-controls="nav-search-panel"
        aria-expanded={searchOpen}
        aria-autocomplete="list"
        placeholder={searchPlaceholder}
        class="placeholder:text-fg-subtle text-fg w-24 bg-transparent text-sm outline-none transition-[width] sm:w-32 md:w-40 lg:w-44"
        autocomplete="off"
        spellcheck="false"
      />
    </div>

    {#if searchOpen}
      <div
        id="nav-search-panel"
        bind:this={searchPanel}
        use:anchoredPopover={{ anchor: searchWrap, align: "right" }}
        data-search-results
        data-topbar-popover
        role="listbox"
        class="border-border bg-bg-elev z-40 flex w-[min(36rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-xl border shadow-2xl"
      >
        <NavSearchPanel
          {query}
          {active}
          onSelect={go}
          onHover={(i) => (active = i)}
          onFilteredChange={(n) => (filteredCount = n)}
        />
        <div
          class="border-border text-fg-subtle flex shrink-0 items-center justify-between border-t px-4 py-2 t-meta"
        >
          <span>↑ ↓ {t("cmdk.hint.navigate", "navigieren")}</span>
          <span>↵ {t("cmdk.hint.open", "öffnen")}</span>
          <span>Esc {t("cmdk.hint.close", "schließen")}</span>
        </div>
      </div>
    {/if}
  </div>

  <div class="flex shrink-0 items-center gap-1">
    {#if nav.actions}
      {@render nav.actions()}
    {/if}
    <!-- Datei-Verschiebungs-Indikator: sitzt im Right-Cluster der Pille
         neben dem Profil-Icon. alwaysShow auf /files (Einsprung in den
         Verlauf), anderswo nur sichtbar wenn Jobs laufen. -->
    <MediaJobsPanel alwaysShow={router.current.startsWith("/files")} />
    <!-- Einstellungen: der eine Einstieg in alles Einstellbare, auch
         Anmeldung, Push und Erscheinungsbild (bis 03.10.2026 stand hier
         ein Konto-Symbol, das auf die Profil-Uebersicht fuehrte).
         Etwas kraeftiger (text-fg-muted) als die uebrigen subtle-Icons. -->
    <a
      href="#/einstellungen"
      class="text-fg-muted hover:text-fg hover:bg-bg-muted/60 flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition {router.current.startsWith(
        '/einstellungen',
      )
        ? 'text-accent'
        : ''}"
      title={t("shell.settings", "Einstellungen")}
      aria-label={t("shell.settings", "Einstellungen")}
    >
      <Icon name="settings" size={16} />
    </a>
  </div>

  <!-- Page-Info als zentriertes Overlay. Trigger ist das Brand-Schild
       links; auf infolosen Pages steht ein Fallback-Text, damit der
       Einstieg auf JEDER Page konsistent existiert. Gleiches Modal-Muster
       wie im Rest der App (Backdrop + zentrierte Karte). backdrop-blur ist
       hier unkritisch (transientes Modal, kein persistenter Bar ueber dem
       driftenden Hintergrund, s. lesson_safari_animated_blur). -->
  {#if openMenu === "info"}
    <div
      use:portal
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      role="presentation"
      transition:fade={{ duration: 120 }}
    >
      <div
        data-topbar-popover
        class="bg-bg-elev border-border w-full max-w-2xl rounded-2xl border p-5 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={nav.infoTitle ?? t("nav.pageinfo", "Über diese Seite")}
        transition:fly={{ y: -12, duration: 160 }}
      >
        <div class="mb-3 flex items-center justify-between gap-3">
          <span class="t-eyebrow text-fg-subtle">
            {nav.infoTitle ?? t("nav.pageinfo", "Über diese Seite")}
          </span>
          <button
            type="button"
            onclick={() => (openMenu = null)}
            aria-label={t("common.close", "Schließen")}
            class="text-fg-subtle hover:text-fg hover:bg-bg-muted/60 -mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="text-fg-muted max-h-[70vh] space-y-2 overflow-y-auto text-sm">
          {#if nav.info}
            {@render nav.info()}
          {:else}
            <p>{t("nav.pageinfo.empty", "Für diese Seite gibt es keine zusätzliche Info.")}</p>
          {/if}
        </div>
      </div>
    </div>
  {/if}
</header>
